// TODO MJR legacy-tasks - Still used by 3 components. Migrate methods to TasksService/TaskListService.
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { TaskList, Task, TaskStatus, TaskResult, TaskListResult, Todo } from '@tasks/tasks.types';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { Project } from 'app/modules/projects/projects.types';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { TaskState } from '@tasks/state/state';
import { TASK_WORKFLOW_STATUSES } from '@tasks/services/task-status.service';

@Injectable({
  providedIn: 'root',
})
export class LegacyTasksTabService {
  private _taskList: BehaviorSubject<TaskList | null>;
  private _taskLists: BehaviorSubject<TaskList[] | null>;
  private _tasks: BehaviorSubject<Task[] | null>;
  private _task: BehaviorSubject<Task | null>;
  private _moduleId: string;
  private _moduleBaseUrl: string;

  workflowStatuses = TASK_WORKFLOW_STATUSES;

  constructor(
    private _httpClient: HttpClient,
    private store: Store,
    private taskExportUtilsService: TaskExportUtilsService,
  ) {
    // Set the private defaults
    this._taskList = new BehaviorSubject(null);
    this._taskLists = new BehaviorSubject(null);
    this._tasks = new BehaviorSubject(null);
    this._task = new BehaviorSubject(null);
    this._moduleId = '';
    this._moduleBaseUrl = '';
  }

  get tasks$(): Observable<Task[]> {
    return this._tasks.asObservable();
  }

  get _accountId() {
    const account = this.store.selectSnapshot(AccountState.getAccount);
    return account.id;
  }

  /**
   * @deprecated Use the this.store.dispatch(new TaskActions.SetRelatedTo(route.data.taskModuleRelatedTo, +relatedToId)) instead;
   */
  set moduleId(val: string) {
    this._moduleId = val;
  }

  /**
   * @deprecated Use the this.store.dispatch(new TaskActions.SetRelatedTo(route.data.taskModuleRelatedTo, +relatedToId)) instead;
   */
  get moduleId() {
    return this._moduleId;
  }

  get apiBaseUrl() {
    if (this.moduleBaseUrl?.includes('custom_objects')) {
      return `${this.moduleBaseUrl}/${this.moduleId}`;
    } else {
      return this.store.selectSnapshot(TaskState.getApiBaseUrl);
    }
  }

  private readonly shallowUrl = 'tasks';

  private buildTasksParams(taskListId?: number, templateId?: number): Record<string, any> {
    const params: Record<string, any> = { account_id: this._accountId };

    if (taskListId != null) {
      params['task_list_id'] = taskListId;
    }
    if (templateId) {
      params['task_list_template_id'] = templateId;
    }

    return params;
  }

  /**
   * @deprecated Use the this.store.dispatch(new TaskActions.SetRelatedTo(route.data.taskModuleRelatedTo, +relatedToId)) instead;
   */
  set moduleBaseUrl(val: string) {
    this._moduleBaseUrl = val;
  }

  /**
   * @deprecated Use the this.store.dispatch(new TaskActions.SetRelatedTo(route.data.taskModuleRelatedTo, +relatedToId)) instead;
   */
  get moduleBaseUrl() {
    return this._moduleBaseUrl;
  }

  /**
   * @deprecated This method will be removed in future versions.
   * Use the new TaskListsService method instead.
   */
  getTaskLists(): Observable<TaskListResult> {
    const url = `${this.apiBaseUrl}/task_lists`;
    return this._httpClient.get<TaskListResult>(url).pipe(
      tap(({ task_lists }) => {
        this._taskLists.next(task_lists);
      }),
    );
  }

  getTaskListById(taskListId: string, templateId?: number, include_tasks = false): Observable<TaskList> {
    let url = `${this.apiBaseUrl}/task_lists/${taskListId}`;
    if (templateId) {
      url = `${url}?task_list_template_id=${templateId}`;
    }

    include_tasks = include_tasks && this.apiBaseUrl.includes('projects'); // Only include tasks for projects
    url = include_tasks ? `${url}?include_tasks=true` : url;

    return this._httpClient.get<any>(url).pipe(
      map((response) => {
        this._taskList.next(response.task_list);
        if (include_tasks) {
          const tasks = this.deepTaskListProjectTransform(response.task_list, response?.project);
          response.task_list.tasks = tasks;
          this._tasks.next(tasks);
        }
        return response.task_list;
      }),
    );
  }

  getTasks(
    taskListId: number,
    templateId?: number,
    filters: any = {},
    format: string = '',
    updateTasks: boolean = true,
    select: string = null,
  ): Observable<Task[]> {
    const {
      workflowStateIn = [],
      assignedUserIdIn = [],
      dueDateGteq = '',
      dueDateLteq = '',
      taskTypeIn = [],
    } = filters;

    if (taskTypeIn.length > 0) {
      taskTypeIn.push('section');
    }

    const params = {
      ...this.buildTasksParams(taskListId, templateId),
      workflow_state_in: workflowStateIn.join(','),
      assigned_user_id_in: assignedUserIdIn.join(','),
      due_date_gteq: dueDateGteq,
      due_date_lteq: dueDateLteq,
      task_type_in: taskTypeIn.join(','),
      format: format,
    };

    if (select) {
      params['select'] = select;
    }

    return this._httpClient.get<any>(this.shallowUrl, { params }).pipe(
      map((response) => {
        if (format && response && (format == 'csv' || format == 'pdf')) {
          if (response?.data?.message) {
            this.taskExportUtilsService.onExportResultWithMessage(response.data.message);
          }

          return response.data;
        }
        let tasks = response.tasks || response.results;
        if (updateTasks) {
          this._tasks.next(tasks);
        }

        return tasks;
      }),
    );
  }

  loadTasks(taskListId: number): Observable<Task[]> {
    const params = this.buildTasksParams(taskListId);

    return this._httpClient.get<any>(this.shallowUrl, { params }).pipe(
      map((response) => {
        let tasks = response.tasks;
        this._tasks.next(tasks);
        return tasks;
      }),
    );
  }

  createTask(
    taskListId: number,
    task: Task,
    pos?: number,
    isSubtask?: boolean,
    updateSubject = true,
    append = true,
  ): Observable<Task> {
    task.workflow_state = task.workflow_state ?? 'not_started';

    const params = this.buildTasksParams(taskListId);

    return this.tasks$.pipe(
      take(1),
      switchMap((tasks) =>
        this._httpClient.post<any>(this.shallowUrl, { task }, { params }).pipe(
          map((response) => {
            let newTask = response.task;
            if (updateSubject) {
              if (!isSubtask) {
                if (pos && tasks) {
                  tasks.splice(pos, 0, newTask);
                } else if (tasks) {
                  if (append) {
                    tasks = [...tasks, newTask];
                  }
                } else {
                  tasks = [newTask];
                }
                this._tasks.next(tasks);
              }
            }
            return newTask;
          }),
        ),
      ),
    );
  }

  /**
   * @deprecated This method will be removed in future versions.
   * Please use TasksService.updateTaskById instead.
   */
  updateTask(
    taskListId: number,
    templateId: number,
    task: Task,
    isSubtask?: boolean,
    updateSubject = true,
  ): Observable<Task> {
    const params = this.buildTasksParams(taskListId, templateId);

    return this.tasks$.pipe(
      take(1),
      switchMap((tasks) =>
        this._httpClient.put<any>(`${this.shallowUrl}/${task.id}`, { task }, { params }).pipe(
          map((response: { task: Task }) => {
            // Find the index of the updated task
            const index = tasks.findIndex((item) => item.id === task.id);
            let updatedTask = response.task;
            updatedTask.todos = updatedTask?.todos?.sort((a: Todo, b: Todo) => a.rank - b.rank);
            if (index === -1) {
              this.handleSubTaskUpdate(updatedTask, tasks);
            }

            // Update the task
            tasks[index] = updatedTask;
            // Update the tasks
            if (updateSubject) {
              this._tasks.next(tasks);
            }

            if (this._task.value?.id === updatedTask.id) {
              this._task.next(updatedTask);
            }

            return updatedTask;
          }),
        ),
      ),
    );
  }

  deleteTask(taskListId: number, templateId: number, id: number, updateSubject = true): Observable<any> {
    const params = this.buildTasksParams(taskListId, templateId);

    return this._httpClient.delete<any>(`${this.shallowUrl}/${id}`, { params }).pipe(
      map((reponse) => {
        return reponse.task;
      }),
    );
  }

  getTaskStatuses(): Observable<TaskStatus[]> {
    let statuses = this.workflowStatuses;

    return of(statuses);
  }

  /**
   * @deprecated This method will be removed in future versions.
   * Please use the new flatTaskArr from the takss.utils.js.
   **/
  flattenTasks(tasks: Task[]): Task[] {
    return (
      tasks?.reduce((acc, task) => {
        if (task?.children) {
          acc?.push(task, ...this.flattenTasks(task?.children));
        } else {
          acc?.push(task);
        }
        return acc;
      }, []) ?? []
    );
  }

  search(filter: {
    fjson: string;
    sort: { field: string; order: string }[];
    q: string;
    include_keydates?: boolean;
    select?: string;
  }): Observable<Task[]> {
    // let's keep it just in case
    // const fjson = JSON.parse(filter.fjson || '[]');
    // fjson.push(['task_type', 'in', ['task']]);
    // const newFilter = { ...filter, fjson: JSON.stringify(fjson) };

    const params = this.buildTasksParams();

    return this._httpClient.post(`${this.shallowUrl}/search`, filter, { params }).pipe(
      map((response: TaskResult) =>
        // include all task_type in the search result filter.
        response.results.filter(
          ({ task_type }) =>
            ['task', 'section', 'key_date'].includes(task_type) ||
            (filter?.include_keydates ? task_type === 'key_date' : false),
        ),
      ),
    );
  }

  private deepTaskListProjectTransform(taskList: TaskList, project: Project): Task[] {
    return (
      taskList?.tasks?.reduce?.((acc, task) => {
        const transformedTask = this.setTaskAttributes(task, taskList, project);
        return [...acc, transformedTask];
      }, []) || []
    );
  }

  private setTaskAttributes(task: Task, taskList: TaskList, project: Project): Task {
    task.project = project;
    task.project_id = +project?.id;
    task.task_list_id = taskList.id;
    task.task_list_name = taskList.name;

    task.children = task.children?.map((child) => {
      return this.setTaskAttributes(child, taskList, project);
    });

    return task;
  }

  private handleSubTaskUpdate(updatedTask: Task, tasks: Task[]): void {
    const flattenedTasks = this.flattenTasks(tasks);
    let pathItems = [updatedTask.id];
    let pointer = updatedTask.id;
    let tempTask: Task = null;

    while ((tempTask = flattenedTasks.find(({ id }) => id === pointer))) {
      if (!tempTask.parent_id) {
        break;
      }

      pathItems.push(tempTask.parent_id);
      pointer = tempTask.parent_id;
    }

    pathItems = pathItems.filter((pathItem) => pathItem !== updatedTask.id).reverse();
    tempTask = null;

    for (const pathItem of pathItems) {
      tempTask = (tempTask?.children ?? tasks).find(({ id }) => id === pathItem);
      let index = -1;
      if ((index = tempTask.children.findIndex(({ id }) => id === updatedTask.id)) > -1) {
        tempTask.children[index] = { ...tempTask.children[index], ...updatedTask };
      }
    }
  }
}
